import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  Modal,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { COLOR_RED, COLOR_PRIMARY } from 'src/utils/const';
import { getSeasonalityBarColor } from 'src/utils/functions';
import ImageInput from '../Inputs/ImageInput';
import { ReportTypes } from '../../Pages/EstimatorPage/propTypes';

const REPORT_PATH_OPTIONS: { label: string; value: ReportTypes }[] = [
  { label: 'Investment Report', value: 'buy' },
  { label: 'Rental Arbitrage Report', value: 'rent' },
  { label: 'Property Management Report', value: 'manage' },
];

interface DownloadReportModalProps {
  open: boolean;
  onClose: () => void;
  onDownload: (settings: {
    logo?: string;
    propertyImage?: string;
    createdBy: string;
    seasonality: string | null;
    trend: number | null;
    percentile: string;
    reportPath: ReportTypes;
  }) => void;
  userName: string;
  isDownloading?: boolean;
  seasonality: number | null | 'loading';
  trend: number | null | 'loading';
  percentile: string;
  hasCompsListings: boolean;
  downloadablePdfUrl: string | null;
  organizationLogo: string | null;
  currentReportType: ReportTypes;
}

const DownloadReportModal: React.FC<DownloadReportModalProps> = ({
  open,
  onClose,
  onDownload,
  userName,
  isDownloading = false,
  seasonality,
  trend,
  percentile,
  hasCompsListings,
  downloadablePdfUrl,
  organizationLogo,
  currentReportType,
}) => {
  const [createdBy, setCreatedBy] = useState(userName);
  const [logo, setLogo] = useState<string[]>([]);
  const [propertyImage, setPropertyImage] = useState<string[]>([]);
  const [reportPath, setReportPath] = useState<ReportTypes>(currentReportType);

  const handleDownload = () => {
    onDownload({
      logo: logo[0] || undefined,
      propertyImage: propertyImage[0] || undefined,
      createdBy,
      seasonality:
        seasonality == 'loading' || seasonality == null
          ? null
          : getSeasonalityBarColor(seasonality, true),
      trend: trend == 'loading' || trend == null ? null : trend,
      percentile,
      reportPath,
    });
  };

  const isDisabled = !hasCompsListings || isDownloading;

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          border: 'none',
          outline: 'none',
          width: 'clamp(300px, 100%, 500px)',
          padding: '30px',
          borderRadius: '12px',
          background: '#fff',
          position: 'relative',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: '4px',
            right: '4px',
          }}
        >
          <CloseRoundedIcon sx={{ color: COLOR_RED }} />
        </IconButton>

        {downloadablePdfUrl ? (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6">Report Generated Successfully</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1" color="text.secondary">
                Your report is now ready for viewing. Click the button below to open it.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                component="a"
                href={downloadablePdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewRoundedIcon />}
                sx={{ textTransform: 'none', color: 'white' }}
                fullWidth
              >
                Open Report PDF
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                If the report does not open automatically, please click the button above.
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Brand Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Created By"
                value={createdBy}
                onChange={e => setCreatedBy(e.target.value)}
                placeholder="Enter name"
              />
            </Grid>

            {organizationLogo && (
              <Grid item xs={12}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                  Using Organization Logo
                </Typography>
                <Box
                  component="img"
                  src={organizationLogo}
                  alt="Organization Logo"
                  sx={{
                    width: '150px',
                    height: 'auto',
                    objectFit: 'contain',
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    p: 1,
                    mt: 0,
                    pt: 0,
                  }}
                />
              </Grid>
            )}

            <Grid container item xs={12} direction="column" spacing={2}>
              <ImageInput cta="Upload Logo" images={logo} setImages={setLogo} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
                Property Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Property Image
              </Typography>
            </Grid>

            <Grid container item xs={12} direction="column" spacing={2}>
              <ImageInput
                cta="Upload property image"
                images={propertyImage}
                setImages={setPropertyImage}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
                Report Type
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {REPORT_PATH_OPTIONS.map(opt => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    variant={reportPath === opt.value ? 'filled' : 'outlined'}
                    onClick={() => setReportPath(opt.value)}
                    sx={{
                      fontWeight: 600,
                      fontSize: '13px',
                      ...(reportPath === opt.value
                        ? {
                            background: COLOR_PRIMARY,
                            color: '#fff',
                            '&:hover': { background: '#026b7d' },
                          }
                        : {
                            borderColor: '#d1d5db',
                            color: '#4b5563',
                            '&:hover': { background: '#f3f4f6' },
                          }),
                    }}
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleDownload}
                disabled={isDisabled}
                sx={{
                  background: COLOR_PRIMARY,
                  '&:hover': {
                    background: COLOR_PRIMARY,
                  },
                }}
              >
                {isDownloading ? <CircularProgress size={24} color="inherit" /> : 'Download Report'}
              </Button>
              {isDisabled && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    color: 'text.secondary',
                    textAlign: 'center',
                  }}
                >
                  Please wait while we load comps for this report...
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      </Box>
    </Modal>
  );
};

export default DownloadReportModal;
